/**
 * OData Service - Generic framework for all Infor LN APIs.
 *
 * Provides:
 * - Entity Registry (all available Entities)
 * - OData Query Builder (client-side)
 * - API Calls for Entities and their fields
 */

import axios from 'axios';

// Use the same base URL as the main API client
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const TOKEN_STORAGE_KEY = 'r4s_omnimens_tokens';

// Create a simple axios instance with auth
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const storedTokens = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (storedTokens) {
    try {
      const tokens = JSON.parse(storedTokens);
      if (tokens.access) {
        config.headers.Authorization = `Bearer ${tokens.access}`;
      }
    } catch { /* ignore */ }
  }
  return config;
});

// ============ Types ============

export interface EntityField {
  name: string;
  dataType: 'string' | 'integer' | 'number' | 'boolean' | 'date' | 'datetime' | 'time' | 'binary';
  edmType: string;
  nullable: boolean;
  maxLength?: number;
}

export interface EntityRelation {
  name: string;
  targetEntity: string;
  isCollection: boolean;
}

export interface EntityDefinition {
  name: string;
  entitySet: string;
  fields: EntityField[];
  relations: EntityRelation[];
  keyFields: string[];
}

export interface EntitiesResponse {
  connection_id: string;
  connection_name: string;
  entity_count: number;
  entities: EntityDefinition[];
}

export interface ODataFilter {
  field: string;
  operator: 'eq' | 'ne' | 'gt' | 'ge' | 'lt' | 'le' | 'contains' | 'startswith' | 'endswith';
  value: string | number | boolean | null;
  logic?: 'and' | 'or';
}

export interface ODataOrderBy {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ODataExpand {
  name: string;
  select?: string[];
  filter?: string;
  orderby?: string;
  expand?: ODataExpand[];
}

export interface ODataQueryConfig {
  select?: string[];
  filter?: ODataFilter[];
  orderby?: ODataOrderBy[];
  expand?: ODataExpand[];
  top?: number;
  skip?: number;
  count?: boolean;
}

// ============ API Functions ============

/**
 * Load all available Entities for a Connection.
 */
export async function fetchEntities(connectionId: string, refresh = false): Promise<EntitiesResponse> {
  const params = new URLSearchParams({ connection_id: connectionId });
  if (refresh) params.append('refresh', 'true');

  const response = await api.get(`/api/entities/?${params}`);
  return response.data;
}

/**
 * Load details of a specific entity.
 */
export async function fetchEntityDetails(entityName: string, connectionId: string): Promise<EntityDefinition> {
  const response = await api.get(`/api/entities/${entityName}/?connection_id=${connectionId}`);
  return response.data;
}

/**
 * Search for entities.
 */
export async function searchEntities(query: string, connectionId: string): Promise<{ name: string; entitySet: string; fieldCount: number }[]> {
  const response = await api.get(`/api/entities/search/?q=${encodeURIComponent(query)}&connection_id=${connectionId}`);
  return response.data.entities;
}

// ============ OData Query Builder (Client-Side) ============

/**
 * Fluent Query Builder for OData URLs.
 *
 * @example
 * const query = new ODataQueryBuilder('SalesOrders')
 *   .select(['SalesOrder', 'CustomerName'])
 *   .filter([{ field: 'Status', operator: 'eq', value: 'Open' }])
 *   .orderby([{ field: 'OrderDate', direction: 'desc' }])
 *   .top(50)
 *   .build();
 */
export class ODataQueryBuilder {
  private params: Map<string, string> = new Map();

  constructor(private endpoint: string) {}

  select(fields: string[]): this {
    if (fields.length > 0) {
      this.params.set('$select', fields.join(','));
    }
    return this;
  }

  filter(filters: ODataFilter[]): this {
    if (filters.length === 0) return this;

    const parts = filters.map((f, i) => {
      const part = this.buildFilterPart(f);
      if (i === 0) return part;
      return `${f.logic || 'and'} ${part}`;
    });

    this.params.set('$filter', parts.join(' '));
    return this;
  }

  private buildFilterPart(f: ODataFilter): string {
    const { field, operator, value } = f;

    // String functions
    if (['contains', 'startswith', 'endswith'].includes(operator)) {
      return `${operator}(${field},'${this.escapeValue(String(value))}')`;
    }

    // Null check
    if (value === null) {
      return `${field} ${operator} null`;
    }

    // String values
    if (typeof value === 'string') {
      return `${field} ${operator} '${this.escapeValue(value)}'`;
    }

    // Boolean
    if (typeof value === 'boolean') {
      return `${field} ${operator} ${value}`;
    }

    // Numbers
    return `${field} ${operator} ${value}`;
  }

  private escapeValue(value: string): string {
    return value.replace(/'/g, "''");
  }

  orderby(orders: ODataOrderBy[]): this {
    if (orders.length > 0) {
      const parts = orders.map(o => `${o.field} ${o.direction}`);
      this.params.set('$orderby', parts.join(','));
    }
    return this;
  }

  expand(entities: ODataExpand[]): this {
    if (entities.length === 0) return this;

    const parts = entities.map(e => this.buildExpandPart(e));
    this.params.set('$expand', parts.join(','));
    return this;
  }

  private buildExpandPart(entity: ODataExpand): string {
    let part = entity.name;
    const options: string[] = [];

    if (entity.select?.length) {
      options.push(`$select=${entity.select.join(',')}`);
    }
    if (entity.filter) {
      options.push(`$filter=${entity.filter}`);
    }
    if (entity.orderby) {
      options.push(`$orderby=${entity.orderby}`);
    }
    if (entity.expand?.length) {
      const nested = entity.expand.map(e => this.buildExpandPart(e)).join(',');
      options.push(`$expand=${nested}`);
    }

    if (options.length > 0) {
      part += `(${options.join(';')})`;
    }
    return part;
  }

  top(limit: number): this {
    if (limit > 0) {
      this.params.set('$top', String(limit));
    }
    return this;
  }

  skip(offset: number): this {
    if (offset > 0) {
      this.params.set('$skip', String(offset));
    }
    return this;
  }

  paginate(page: number, pageSize: number): this {
    this.params.set('$top', String(pageSize));
    if (page > 1) {
      this.params.set('$skip', String((page - 1) * pageSize));
    }
    return this;
  }

  count(enabled = true): this {
    if (enabled) {
      this.params.set('$count', 'true');
    }
    return this;
  }

  getParams(): Record<string, string> {
    return Object.fromEntries(this.params);
  }

  build(): string {
    if (this.params.size === 0) {
      return `/${this.endpoint}`;
    }
    const queryString = Array.from(this.params.entries())
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    return `/${this.endpoint}?${queryString}`;
  }

  buildParamsOnly(): string {
    if (this.params.size === 0) return '';
    return Array.from(this.params.entries())
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
  }
}

/**
 * Convenience: Build query from config object.
 */
export function buildQueryFromConfig(endpoint: string, config: ODataQueryConfig): string {
  const builder = new ODataQueryBuilder(endpoint);

  if (config.select) builder.select(config.select);
  if (config.filter) builder.filter(config.filter);
  if (config.orderby) builder.orderby(config.orderby);
  if (config.expand) builder.expand(config.expand);
  if (config.top) builder.top(config.top);
  if (config.skip) builder.skip(config.skip);
  if (config.count) builder.count();

  return builder.build();
}

// ============ Entity Grouping (for UI) ============

export type EntityGroup = 'sales' | 'purchase' | 'inventory' | 'production' | 'finance' | 'master' | 'other';

const ENTITY_GROUPS: Record<EntityGroup, { label: string; patterns: string[] }> = {
  sales: {
    label: 'Sales',
    patterns: ['Sales', 'Customer', 'Quote', 'Quotation', 'Order'],
  },
  purchase: {
    label: 'Purchasing',
    patterns: ['Purchase', 'Supplier', 'Vendor', 'Requisition'],
  },
  inventory: {
    label: 'Inventory',
    patterns: ['Item', 'Warehouse', 'Inventory', 'Stock', 'Location'],
  },
  production: {
    label: 'Production',
    patterns: ['Production', 'Manufacturing', 'BOM', 'Bill', 'Routing', 'Work'],
  },
  finance: {
    label: 'Finance',
    patterns: ['Invoice', 'Payment', 'Ledger', 'Account', 'Budget', 'Cost'],
  },
  master: {
    label: 'Master Data',
    patterns: ['Business', 'Partner', 'Employee', 'Project', 'Resource'],
  },
  other: {
    label: 'Other',
    patterns: [],
  },
};

/**
 * Group Entity by functional area.
 */
export function getEntityGroup(entityName: string): EntityGroup {
  const nameLower = entityName.toLowerCase();

  for (const [group, { patterns }] of Object.entries(ENTITY_GROUPS)) {
    if (patterns.some(p => nameLower.includes(p.toLowerCase()))) {
      return group as EntityGroup;
    }
  }

  return 'other';
}

/**
 * Group list of entities by functional area.
 */
export function groupEntities(entities: EntityDefinition[]): Record<EntityGroup, EntityDefinition[]> {
  const grouped: Record<EntityGroup, EntityDefinition[]> = {
    sales: [],
    purchase: [],
    inventory: [],
    production: [],
    finance: [],
    master: [],
    other: [],
  };

  for (const entity of entities) {
    const group = getEntityGroup(entity.name);
    grouped[group].push(entity);
  }

  // Sort each group alphabetically
  for (const group of Object.keys(grouped) as EntityGroup[]) {
    grouped[group].sort((a, b) => a.name.localeCompare(b.name));
  }

  return grouped;
}

/**
 * Get label for entity group.
 */
export function getGroupLabel(group: EntityGroup): string {
  return ENTITY_GROUPS[group]?.label || group;
}
